"use client";

import { PackagePaySelect } from "../package-pay-select";
import { SellPackageButton } from "../sell-package-dialog";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { useAppointmentModal } from "@/contexts/AppointmentModalContext";
import { useComputedPayment } from "@/hooks/modal/useComputedPayment";
import { formatCurrency } from "@/lib/utils";
// Centralized cost calculation engine - single source of truth
import {
  getServiceUnitPrice,
  getServiceLineTotal,
  applyQuantityChange,
  applyDiscountChange,
  applyManualPriceChange,
  calculateServiceCostFields,
  round2,
} from "@/lib/appointment/costEngine";
import { ServicePaymentPanel } from "./service-payment-panel";
import { PaymentMethodCard } from "./payment-method-card";
import { useAppointmentModalPaymentInfo } from "@/hooks/modal/useAppointmentModalPaymentInfo";
import { Input } from "@/components/ui/input";
import { Banknote, CreditCard, AlertTriangle } from "lucide-react";
import { goToDashboard } from "@/features/sso/goToDashboard";

enum PaymentMode {
  QUICK = "quick",
  SPLIT = "split",
}

type SplitAmounts = Record<number, number>;
export function PaymentSectionMobile() {
  const { appointment, updateAppointment } = useAppointmentModal();
  const { data: backendAppointment } = useAppointmentModalPaymentInfo();
  const payment = useComputedPayment(); // totals/labels
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [paymentMode, setPaymentMode] = useState<PaymentMode>(PaymentMode.QUICK);
  const [splitAmounts, setSplitAmounts] = useState<SplitAmounts>({});
  const [splitEditState, setSplitEditState] = useState<Record<number, string | undefined>>({});
  const { t } = useTranslation();
  const paymentMethods = appointment?.paymentSection?.paymentMethods ?? [];
  const paymentSection = appointment?.paymentSection ?? {};
  const totalAmount = payment?.totalAmount ?? 0;
  const splitTotal = useMemo(
    () =>
      Object.values(splitAmounts).reduce(
        (sum, raw) => sum + (Number.isFinite(Number(raw)) ? Number(raw) : 0),
        0
      ),
    [splitAmounts]
  );
  // Calculate paid_full status directly from appointment data
  const isPaidFull = useMemo(() => {
    // Derive paid state from persisted backend data so the banner only appears on load
    // and recognize "paid" statuses represented by either 1 or 2 (mirrors backend utils).
    const backendPaidFull = backendAppointment?.paid_full ?? backendAppointment?.paymentSection?.paidFull;
    return backendPaidFull === 1 || backendPaidFull === 2 || backendAppointment?.paymentSection?.isPaid;
  }, [backendAppointment]);

  const isSplitMode = paymentMode === PaymentMode.SPLIT;
  const isSplitValid = !isSplitMode || Math.abs(splitTotal - totalAmount) < 0.01;
  const paymentInitRef = useRef(false);

  useEffect(() => {
    if (backendAppointment?.paymentSection) {
      paymentInitRef.current = false;
      setSelectedAccountId(null);
      setSplitAmounts({});
      setSplitEditState({});
      setPaymentMode(PaymentMode.QUICK);
      updateAppointment({ paymentSection: backendAppointment.paymentSection });
    }
  }, [backendAppointment, updateAppointment]);

  useEffect(() => {
    paymentInitRef.current = false;
    setSelectedAccountId(null);
    setSplitAmounts({});
    setSplitEditState({});
    setPaymentMode(PaymentMode.QUICK);
  }, [appointment?.id]);

  useEffect(() => {
    if (!appointment || !paymentMethods.length || paymentInitRef.current) return;

    const initialSplit: SplitAmounts = {};
    paymentMethods.forEach((m: any) => {
      const id = m?.account?.id;
      if (id != null) {
        const paid = Number(m?.account?.paid_amount);
        initialSplit[id] = Number.isFinite(paid) ? paid : 0;
      }
    });

    const paidMethods = paymentMethods.filter((m: any) => Number(m?.account?.paid_amount) > 0);
    const defaultSelected = paidMethods[0]?.account?.id ?? null;

    setSplitAmounts(initialSplit);
    setSelectedAccountId(defaultSelected);
    setPaymentMode(paidMethods.length > 1 ? PaymentMode.SPLIT : PaymentMode.QUICK);
    paymentInitRef.current = true;
  }, [appointment, paymentMethods]);

  useEffect(() => {
    if (!paymentInitRef.current) return;
    if (paymentMode !== PaymentMode.SPLIT) return;
    const hasDiff = paymentMethods.some((m: any) => {
      const id = m?.account?.id;
      const current = Number(m?.account?.paid_amount) || 0;
      const next = id != null ? Number(splitAmounts[id]) || 0 : 0;
      return Math.abs(current - next) >= 0.005;
    });
    if (!hasDiff) return;
    applySplitAmounts(splitAmounts);
  }, [paymentMode, splitAmounts, paymentMethods]);

  const applyPaymentSection = (methods: any[], mode: PaymentMode = paymentMode) => {
    const areMethodsEqual = (left: any[], right: any[]) => {
      if (left.length !== right.length) return false;
      return left.every((m, idx) => {
        const n = right[idx];
        const mId = m?.account?.id;
        const nId = n?.account?.id;
        if (mId !== nId) return false;
        const mPaid = Number(m?.account?.paid_amount) || 0;
        const nPaid = Number(n?.account?.paid_amount) || 0;
        return Math.abs(mPaid - nPaid) < 0.005;
      });
    };

    const paidTotal = methods.reduce(
      (sum, m) => sum + (Number(m?.account?.paid_amount) || 0),
      0
    );
    const matchesTotal = Math.abs(paidTotal - totalAmount) < 0.01;
    const paidFull = paidTotal > 0 ? (matchesTotal ? 1 : 2) : 0;

    if (
      areMethodsEqual(paymentMethods, methods) &&
      paymentSection?.isPaid === (paidTotal > 0) &&
      paymentSection?.paidFull === paidFull &&
      paymentSection?.paymentMode === mode &&
      appointment?.paid_full === paidFull &&
      appointment?.is_paid === (paidTotal > 0)
    ) {
      return;
    }

    updateAppointment({
      paymentSection: {
        ...paymentSection,
        paymentMethods: methods,
        isPaid: paidTotal > 0,
        paidFull,
        paymentMode: mode,
      },
      paid_full: paidTotal > 0 ? paidFull : undefined,
      is_paid: paidTotal > 0,
    });
  };

  const applyQuickSelection = (accountId: number | null) => {
    if (!accountId || !paymentMethods.length) return;
    setSelectedAccountId(accountId);
    const updatedMethods = paymentMethods.map((m: any) =>
      m?.account
        ? {
          ...m,
          account: {
            ...m.account,
            paid_amount: m.account.id === accountId ? totalAmount : 0,
          },
        }
        : m
    );
    applyPaymentSection(updatedMethods, PaymentMode.QUICK);
  };

  const applySplitAmounts = (amounts: SplitAmounts = splitAmounts) => {
    if (!paymentMethods.length) return;
    const updatedMethods = paymentMethods.map((m: any) =>
      m?.account
        ? {
          ...m,
          account: {
            ...m.account,
            paid_amount: Number(amounts[m.account.id]) || 0,
          },
        }
        : m
    );
    applyPaymentSection(updatedMethods, PaymentMode.SPLIT);
  };

  const handleSplitAmountChange = (accountId: number, value: string) => {
    setSplitEditState((prev) => ({ ...prev, [accountId]: value }));
  };

  const applySplitAmountUpdate = (accountId: number, rawValue: string) => {
    const rawCleaned = String(rawValue ?? "").replace(",", ".");
    const numeric = Number(rawCleaned);
    const nextValue = Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;

    setSplitAmounts((prev) => ({ ...prev, [accountId]: nextValue }));
    setSplitEditState((prev) => {
      const next = { ...prev };
      delete next[accountId];
      return next;
    });
  };

  const handleSplitInputFocus = (accountId: number) => {
    setSplitEditState((prev) => {
      if (splitAmounts[accountId] === 0 || splitAmounts[accountId] === undefined) {
        return { ...prev, [accountId]: "" };
      }
      const currentAmount = splitAmounts[accountId] ?? 0;
      return { ...prev, [accountId]: currentAmount.toString() };
    });
  };

  const handleSplitInputBlur = (accountId: number, rawValue: string) => {
    applySplitAmountUpdate(accountId, rawValue);
  };

  const services = Array.isArray(appointment?.services) ? appointment!.services : [];

  // Helper to update service at index using centralized cost engine
  const updateServiceAt = (idx: number, patch: any) => {
    if (!appointment?.services) return;

    const oldService = appointment.services[idx];

    // Detect if this is a quantity-only change (no discount or manual_cost in patch)
    const isQuantityOnlyChange =
      (patch.amount !== undefined || patch.quantity !== undefined) &&
      patch.discount === undefined &&
      patch.manual_cost === undefined &&
      patch.cost_to_pay === undefined;

    let costFields;

    if (isQuantityOnlyChange) {
      // Use centralized applyQuantityChange which properly handles manual_cost
      const newQuantity = patch.amount ?? patch.quantity ?? oldService.amount ?? 1;
      costFields = applyQuantityChange(oldService as any, newQuantity);
    } else {
      // For discount/price changes, use calculateServiceCostFields
      const mergedService = { ...oldService, ...patch };
      costFields = calculateServiceCostFields({
        cost: Number(oldService.cost) || getServiceUnitPrice(oldService),
        amount: mergedService.amount ?? mergedService.quantity ?? 1,
        discount: mergedService.discount ?? 0,
        manualCost: mergedService.manual_cost,
        firstCost: oldService.first_cost ?? undefined,
        isCreation: false,
      });
    }

    // Construct the fully updated service object
    const nextService = {
      ...oldService,
      ...patch,
      ...costFields,
      price: costFields.cost_to_pay,
      // Preserve quantity alias if it existed
      ...(Object.prototype.hasOwnProperty.call(oldService, 'quantity')
        ? { quantity: costFields.amount }
        : {}),
    };

    // Compose new services array
    const nextServices = appointment.services.map((s: any, i: number) => (i === idx ? nextService : s));

    // Calculate new Grand Total for payment sync
    const newTotalAmount = nextServices.reduce((sum: number, s: any) => sum + (Number(s.cost_to_pay) || 0), 0);
    const roundedTotal = round2(newTotalAmount);

    // Update payment methods if in Quick mode
    const currentMethods = appointment.paymentSection?.paymentMethods ?? [];
    let nextMethods = currentMethods;

    if (paymentMode === PaymentMode.QUICK && selectedAccountId != null) {
      nextMethods = currentMethods.map((m: any) => {
        if (m?.account?.id === selectedAccountId) {
          return {
            ...m,
            applicableAmount: roundedTotal,
            account: {
              ...m.account,
              paid_amount: roundedTotal,
            },
          };
        }
        return {
          ...m,
          applicableAmount: 0,
          account: {
            ...m.account,
            paid_amount: 0,
          },
        };
      });
    }

    // Persist to context
    updateAppointment({
      services: nextServices,
      paymentSection: {
        ...(appointment.paymentSection ?? {}),
        paymentMethods: nextMethods,
        paymentMode,
      },
    });
  };

  // Handler for editing line total (direct price change)
  const handleEditLineTotal = (idx: number, rawValue: string) => {
    const rawCleaned = String(rawValue || "").replace(",", ".");
    const targetLineTotal = Math.max(0, Number(rawCleaned) || 0);
    const s = appointment?.services?.[idx];
    if (!s) return;

    // Use centralized applyManualPriceChange from costEngine
    const costPatch = applyManualPriceChange(s as any, targetLineTotal);
    updateServiceAt(idx, costPatch);
  };

  // Handler for editing discount
  const handleEditDiscount = (idx: number, rawValue: number) => {
    const discountVal = Math.max(0, Math.min(100, Number(rawValue) || 0));
    const s = appointment?.services?.[idx];
    if (!s) return;

    // Use centralized applyDiscountChange from costEngine
    const costPatch = applyDiscountChange(s as any, discountVal);
    updateServiceAt(idx, costPatch);
  };

  const handleRemoveService = (id: number | string) => {
    if (!Array.isArray(appointment?.services)) return;
    const updatedServices = appointment.services.filter((service: any) => service.id !== id);
    updateAppointment({ services: updatedServices });
  };

  const specialistName = appointment?.staff?.name;
  const duration = appointment?.detailsSection?.serviceDurationString;

  const panels = useMemo(() => {
    return services.map((s: any, idx: number) => {
      const qty = Number.isFinite(s.amount) ? s.amount : 1;
      // Use centralized helpers for price calculation
      const unitPrice = getServiceUnitPrice(s);
      const discount = Number.isFinite(s.discount) ? s.discount : 0;
      const total = getServiceLineTotal(s);

      // Paid amount: use cost_to_pay if available, otherwise derive from line total
      // For unpaid appointments, paid will be 0
      const paid = Number(s.cost_to_pay) || 0;

      return {
        idx,
        id: s.id ?? `${s.title}-${Math.random().toString(36).slice(2, 8)}`,
        serviceName: s.title ?? "—",
        quantity: qty,
        price: unitPrice,
        discount,
        total,
        paid,
      };
    });
  }, [services]);

  return (
    <div className="space-y-3">
      {panels.length > 0 ? (
        panels.map((p) => (
          <div key={p.id} className="space-y-1">
          <ServicePaymentPanel
            serviceName={p.serviceName}
            specialist={specialistName}
            duration={duration}
            quantity={p.quantity}
            price={p.price}
            discount={p.discount}
            total={p.total}
            paid={p.paid}
            disabled={isPaidFull}
            onQuantityChange={(newQty) => updateServiceAt(p.idx, { amount: newQty })}
            onDiscountChange={(discountPct) => handleEditDiscount(p.idx, discountPct)}
            onPriceChange={(newPrice) => handleEditLineTotal(p.idx, String(newPrice))}
            onRemove={() => handleRemoveService(p.id)}
          />
          <PackagePaySelect serviceId={Number(p.id)} className="flex items-center gap-2 px-1 text-xs text-muted-foreground" />
          </div>
        ))
      ) : (
        <div className="p-4 text-center text-muted-foreground bg-muted/30 rounded">
          {t("appointment.payment.no_services", "No services in appointment")}
        </div>
      )}

      {/* client packages: sell one now, then pay today's service with it */}
      <div className="flex justify-end">
        <SellPackageButton preselect={!isPaidFull} />
      </div>

      {/* Totals */}
      <div className="pt-2 border-t">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">{t("appointment.payment.to_pay", "To pay")}</span>
          <span className="font-medium">{payment?.totalRemainingString ?? "—"}</span>
        </div>
        <div className="mt-1 flex justify-between text-xs text-muted-foreground">
          <span>{t("appointment.payment.visit_price", "Visit price")}</span>
          <span>{payment?.totalAmountString ?? "—"}</span>
        </div>
        <div className="mt-1 flex justify-between text-xs text-muted-foreground">
          <span>{t("appointment.payment.paid", "Paid")}</span>
          <span>{payment?.totalPaidString ?? formatCurrency(0)}</span>
        </div>
      </div>

      {/* Способы оплаты (accounts) */}
      {isPaidFull ? (
        <div className="flex flex-col items-center justify-center py-6 bg-muted/30 rounded-lg border border-dashed">
          <div className="text-xl font-bold text-green-600 mb-1">{t("appointment.payment.paid", "Paid")}</div>
          <div className="text-xs text-muted-foreground">{t("appointment.payment.fully_paid", "Order fully paid")}</div>
          {/* Payment Summary Breakdown */}
          {backendAppointment?.paymentSection?.paymentSummary && (
            <div className="mt-3 w-full">
              <div className="text-xs text-muted-foreground mb-1.5 text-center">{t("appointment.payment.payment_breakdown", "Payment breakdown")}</div>
              <div className="space-y-1">
                {backendAppointment.paymentSection.paymentSummary.map((item: any) => (
                  <div key={item.accountId} className="flex justify-between items-center text-sm px-3">
                    <span className="flex items-center gap-2">
                      {item.isCash ? (
                        <Banknote className="w-4 h-4 text-blue-600" />
                      ) : (
                        <CreditCard className="w-4 h-4 text-green-600" />
                      )}
                      <span className="text-xs">{item.accountTitle}</span>
                    </span>
                    <span className="font-medium text-xs">{formatCurrency(item.totalPaid)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : paymentMethods.length > 0 ? (
        <div className="space-y-3">
          <h3 className="text-base font-medium">{t("appointment.payment.payment_methods", "Payment methods")}</h3>

          <div className="flex border-b text-sm">
            <button
              type="button"
              className={clsx(
                "px-3 py-2 -mb-px border-b-2 transition",
                paymentMode === PaymentMode.QUICK
                  ? "border-primary font-medium"
                  : "border-transparent text-muted-foreground"
              )}
              onClick={() => {
                setPaymentMode(PaymentMode.QUICK);
              }}
            >
              {t("appointment.payment.quick_payment", "Quick payment")}
            </button>
            <button
              type="button"
              className={clsx(
                "px-3 py-2 -mb-px border-b-2 transition",
                paymentMode === PaymentMode.SPLIT
                  ? "border-primary font-medium"
                  : "border-transparent text-muted-foreground"
              )}
              onClick={() => {
                setPaymentMode(PaymentMode.SPLIT);
              }}
            >
              {t("appointment.payment.split_payment", "Split payment")}
            </button>
          </div>

          {paymentMode === PaymentMode.QUICK && (
            <div className="grid grid-cols-2 gap-3">
              {paymentMethods.map((method: any, idx: number) => {
                const accountId = method?.account?.id;
                const key = accountId ?? idx;
                return (
                  <PaymentMethodCard
                    key={key}
                    method={method}
                    selected={accountId != null && selectedAccountId === accountId}
                    onSelect={() => {
                      setPaymentMode(PaymentMode.QUICK);
                      applyQuickSelection(accountId ?? null);
                    }}
                  />
                );
              })}
            </div>
          )}

          {paymentMode === PaymentMode.SPLIT && (
            <div className="space-y-3">
              {paymentMethods.map((method: any, idx: number) => {
                const accountId = method?.account?.id;
                const key = accountId ?? idx;

                return (
                  <div key={key} className="rounded-xl border p-3 space-y-2">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                        {method.account?.isCash ? (
                          <Banknote className="h-5 w-5 text-blue-600" />
                        ) : (
                          <CreditCard className="h-5 w-5 text-green-600" />
                        )}
                      </span>
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {method.account?.isCash ? t("appointment.payment.cash", "Cash") : t("appointment.payment.card", "Bank card")}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {method.account?.title || "—"}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">{t("appointment.payment.amount_currency", "Amount")}</label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        min={0}
                        step="0.01"
                        value={
                          accountId != null
                            ? splitEditState[accountId] !== undefined
                              ? splitEditState[accountId]
                              : (splitAmounts[accountId] ?? 0)
                            : ""
                        }
                        disabled={accountId == null}
                        onChange={(e) => {
                          if (accountId != null) {
                            handleSplitAmountChange(accountId, e.target.value);
                          }
                        }}
                        onFocus={() => {
                          if (accountId != null) {
                            handleSplitInputFocus(accountId);
                          }
                        }}
                        onBlur={(e) => {
                          if (accountId != null) {
                            handleSplitInputBlur(accountId, e.target.value);
                          }
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {isSplitMode && !isSplitValid && (
            <p className="text-xs text-red-500">
              {t("appointment.payment.split_sum_mismatch", "Payment amounts must equal the total")} ({payment?.totalAmountString ?? totalAmount}).
            </p>
          )}
        </div>
      ) : (
        <div className="flex min-h-[180px] flex-col items-center justify-center rounded-lg border border-dashed border-amber-200 bg-amber-50/40 px-4 py-6 text-center">
          <AlertTriangle className="mb-4 h-9 w-9 text-amber-400" strokeWidth={1.5} />
          <p className="mb-2 text-sm font-medium text-foreground">
            {t("appointment.payment.no_payment_accounts", "No payment accounts available")}
          </p>
          <p className="mb-3 text-sm text-muted-foreground">
            {t(
              "appointment.payment.no_payment_accounts_hint",
              "Create payment accounts first to write off the amount."
            )}
          </p>
          <button
            type="button"
            onClick={() => void goToDashboard("/dashboard/payment-accounts")}
            className="text-sm font-medium text-amber-600 underline underline-offset-2 hover:text-amber-700"
          >
            {t("appointment.payment.create_payment_account", "Create payment account")}
          </button>
        </div>
      )}
    </div>
  );
}
